import React from 'react'
import { ACTION_TYPE_LABELS, CoachingResult, CoachingSession, StoredCommunicationUpdate } from '../services/communicationCoachService'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from './LoadingSpinner'
import { MessageBox, useMessageBox } from './MessageBox'
import { exportCoverLetterPdf } from '../utils/communicationCoachExport'
import './CommunicationCoachDisplay.css'

interface CommunicationCoachDisplayProps {
  session: CoachingSession
  isPolling: boolean
  onBack: () => void
  backLabel?: string
  canManageStoredCommunication?: boolean
  initialEditMode?: boolean
  onSaveStoredCommunication?: (updates: StoredCommunicationUpdate) => Promise<void>
  onDeleteStoredCommunication?: () => Promise<void>
}

export const CommunicationCoachDisplay: React.FC<CommunicationCoachDisplayProps> = ({
  session,
  isPolling,
  onBack,
  backLabel = 'Back to Wizard',
  canManageStoredCommunication = false,
  initialEditMode = false,
  onSaveStoredCommunication,
  onDeleteStoredCommunication,
}) => {
  const { profile } = useAuth()
  const { action_type, result, status, quality_tier } = session
  const [isEditing, setIsEditing] = React.useState(false)
  const [editableSubject, setEditableSubject] = React.useState('')
  const [editableBody, setEditableBody] = React.useState('')
  const [editableKeyPoints, setEditableKeyPoints] = React.useState('')
  const [editableCallToAction, setEditableCallToAction] = React.useState('')
  const [editableCoverLetterSubject, setEditableCoverLetterSubject] = React.useState('')
  const [editableCoverLetterText, setEditableCoverLetterText] = React.useState('')
  const [editableCoverLetterKeyPoints, setEditableCoverLetterKeyPoints] = React.useState('')
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [isDownloadingCoverLetterPdf, setIsDownloadingCoverLetterPdf] = React.useState(false)
  const { messageBox, showMessageBox } = useMessageBox()

  const isEmailLikeAction = [
    'email_response',
    'recruiter_outreach',
    'follow_up',
    'thank_you_note',
    'interview_confirmation',
    'offer_response',
  ].includes(action_type)
  const isCoverLetterAction = action_type === 'cover_letter'

  const generatedResponse = result?.generated_response
  const generatedCoverLetter = result?.cover_letter
  const canEditStoredCommunication = Boolean(
    canManageStoredCommunication &&
    (
      (isEmailLikeAction && generatedResponse) ||
      (isCoverLetterAction && generatedCoverLetter)
    ) &&
    onSaveStoredCommunication,
  )

  const resetEditableState = React.useCallback(() => {
    const generated = session.result?.generated_response
    const coverLetter = session.result?.cover_letter
    setEditableSubject(generated?.subject || '')
    setEditableBody(generated?.body || '')
    setEditableKeyPoints((generated?.key_points || []).join('\n'))
    setEditableCallToAction(generated?.call_to_action || '')
    setEditableCoverLetterSubject(coverLetter?.email_subject || '')
    setEditableCoverLetterText(coverLetter?.full_letter || '')
    setEditableCoverLetterKeyPoints((coverLetter?.key_match_points || []).join('\n'))
    setSaveError(null)
  }, [session.result])

  React.useEffect(() => {
    resetEditableState()
    setIsEditing(Boolean(initialEditMode && canEditStoredCommunication))
  }, [session.session_id, initialEditMode, canEditStoredCommunication, resetEditableState])

  const copyText = (value: string) => {
    navigator.clipboard.writeText(value)
  }

  const handleDownloadCoverLetterPdf = async () => {
    const fullLetter = result?.cover_letter?.full_letter
    if (!fullLetter) {
      return
    }

    const authorName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || undefined

    try {
      setIsDownloadingCoverLetterPdf(true)
      await exportCoverLetterPdf(fullLetter, {
        authorName,
        company: session.context?.company,
        position: session.context?.position,
        recruiterName: session.context?.recruiter_name,
      })
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Download Failed',
        message: error instanceof Error ? error.message : 'Failed to generate the cover letter PDF. Please try again.',
        buttons: ['ok'],
        onButtonClick: () => {},
      })
    } finally {
      setIsDownloadingCoverLetterPdf(false)
    }
  }

  const renderListSection = (title: string, items?: string[]) => {
    if (!items || items.length === 0) {
      return null
    }

    return (
      <div className="result-section">
        <h4>{title}</h4>
        <ul>
          {items.map((item, idx) => (
            <li key={`${title}-${idx}`}>{item}</li>
          ))}
        </ul>
      </div>
    )
  }

  const renderEmailLikeResult = (heading: string, coachingResult: CoachingResult) => {
    const generated = coachingResult.generated_response
    if (!generated) {
      return null
    }

    return (
      <div className="result-email-response">
        <h3>{heading}</h3>
        {(generated.subject || isEditing) && (
          <div className="result-section">
            <h4>Subject</h4>
            {isEditing ? (
              <input
                className="result-input"
                value={editableSubject}
                onChange={(event) => setEditableSubject(event.target.value)}
                placeholder="Subject"
              />
            ) : (
              <p>{generated.subject}</p>
            )}
          </div>
        )}
        {(generated.body || isEditing) && (
          <div className="result-section">
            <h4>Body</h4>
            {isEditing ? (
              <textarea
                className="result-textarea"
                value={editableBody}
                onChange={(event) => setEditableBody(event.target.value)}
                rows={16}
              />
            ) : (
              <>
                <div className="result-body">{generated.body}</div>
                <button
                  className="copy-button"
                  onClick={() => copyText(generated.body || '')}
                >
                  Copy to Clipboard
                </button>
              </>
            )}
          </div>
        )}
        {(generated.call_to_action || isEditing) && (
          <div className="result-section">
            <h4>Recommended Next Step</h4>
            {isEditing ? (
              <input
                className="result-input"
                value={editableCallToAction}
                onChange={(event) => setEditableCallToAction(event.target.value)}
                placeholder="Call to action"
              />
            ) : (
              <p>{generated.call_to_action}</p>
            )}
          </div>
        )}
        {generated.recommended_option && (
          <div className="result-section">
            <h4>Recommended Option</h4>
            <p>{generated.recommended_option}</p>
          </div>
        )}
        {isEditing ? (
          <div className="result-section">
            <h4>Key Points</h4>
            <textarea
              className="result-textarea result-textarea-compact"
              value={editableKeyPoints}
              onChange={(event) => setEditableKeyPoints(event.target.value)}
              rows={6}
              placeholder="One key point per line"
            />
          </div>
        ) : (
          renderListSection('Key Points', generated.key_points)
        )}
        {generated.timing_guidance && (
          <div className="result-section">
            <h4>Timing Guidance</h4>
            <p>{generated.timing_guidance}</p>
          </div>
        )}
        {renderListSection('Personalization Hooks', generated.personalization_hooks)}
        {renderListSection('Logistics Checklist', generated.logistics_checklist)}
        {generated.response_options && generated.response_options.length > 0 && (
          <div className="result-section">
            <h4>Response Options</h4>
            <div className="response-option-list">
              {generated.response_options.map((option, idx) => (
                <div key={`response-option-${idx}`} className="variant-card">
                  <strong>{option.label}</strong>
                  {option.summary && <p>{option.summary}</p>}
                  {option.body && <div className="result-body compact">{option.body}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        {generated.alternate_versions && generated.alternate_versions.length > 0 && (
          <div className="result-section">
            <h4>Alternate Versions</h4>
            <div className="response-option-list">
              {generated.alternate_versions.map((version, idx) => (
                <div key={`alternate-version-${idx}`} className="variant-card">
                  <strong>{version.label}</strong>
                  {version.subject && <p><strong>Subject:</strong> {version.subject}</p>}
                  {version.body && <div className="result-body compact">{version.body}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderCoverLetterResult = (coachingResult: CoachingResult) => {
    const coverLetter = coachingResult.cover_letter
    if (!coverLetter) {
      return null
    }

    return (
      <div className="result-cover-letter">
        <h3>Customized Cover Letter</h3>
        {(coverLetter.email_subject || isEditing) && (
          <div className="result-section">
            <h4>Suggested Email Subject</h4>
            {isEditing ? (
              <input
                className="result-input"
                value={editableCoverLetterSubject}
                onChange={(event) => setEditableCoverLetterSubject(event.target.value)}
                placeholder="Suggested email subject"
              />
            ) : (
              <p>{coverLetter.email_subject}</p>
            )}
          </div>
        )}
        {(coverLetter.full_letter || isEditing) && (
          <div className="result-section">
            <h4>Letter</h4>
            {isEditing ? (
              <textarea
                className="result-textarea"
                value={editableCoverLetterText}
                onChange={(event) => setEditableCoverLetterText(event.target.value)}
                rows={18}
              />
            ) : (
              <>
                <div className="result-body">{coverLetter.full_letter}</div>
                <div className="result-actions">
                  <button
                    className="copy-button"
                    onClick={() => copyText(coverLetter.full_letter || '')}
                  >
                    Copy to Clipboard
                  </button>
                  <button
                    className="secondary-button"
                    onClick={handleDownloadCoverLetterPdf}
                    disabled={isDownloadingCoverLetterPdf}
                  >
                    {isDownloadingCoverLetterPdf ? 'Preparing PDF...' : 'Download PDF'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {isEditing ? (
          <div className="result-section">
            <h4>Key Match Points</h4>
            <textarea
              className="result-textarea result-textarea-compact"
              value={editableCoverLetterKeyPoints}
              onChange={(event) => setEditableCoverLetterKeyPoints(event.target.value)}
              rows={5}
              placeholder="One point per line"
            />
          </div>
        ) : (
          renderListSection('Key Match Points', coverLetter.key_match_points)
        )}
        {renderListSection('Application Notes', coverLetter.application_notes)}
        {renderListSection('Customization Notes', coverLetter.customization_notes)}
        {coverLetter.tone_rationale && (
          <div className="result-section">
            <h4>Tone Rationale</h4>
            <p>{coverLetter.tone_rationale}</p>
          </div>
        )}
      </div>
    )
  }

  const handleSave = async () => {
    if (!onSaveStoredCommunication) {
      return
    }

    try {
      setIsSaving(true)
      setSaveError(null)
      if (action_type === 'cover_letter' && result?.cover_letter) {
        await onSaveStoredCommunication({
          result: {
            ...result,
            cover_letter: {
              ...result.cover_letter,
              email_subject: editableCoverLetterSubject.trim(),
              full_letter: editableCoverLetterText,
              key_match_points: editableCoverLetterKeyPoints
                .split('\n')
                .map((value) => value.trim())
                .filter(Boolean),
            },
          },
        })
      } else if (result?.generated_response) {
        await onSaveStoredCommunication({
          result: {
            ...result,
            generated_response: {
              ...result.generated_response,
              subject: editableSubject.trim(),
              body: editableBody,
              key_points: editableKeyPoints
                .split('\n')
                .map((value) => value.trim())
                .filter(Boolean),
              call_to_action: editableCallToAction.trim(),
            },
          },
        })
      } else {
        return
      }
      setIsEditing(false)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to update stored communication')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    resetEditableState()
    setSaveError(null)
    setIsEditing(false)
  }

  const deleteStoredCommunication = async () => {
    if (!onDeleteStoredCommunication) {
      return
    }

    try {
      setIsDeleting(true)
      setSaveError(null)
      await onDeleteStoredCommunication()
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to delete stored communication')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDelete = () => {
    if (!onDeleteStoredCommunication) {
      return
    }

    showMessageBox({
      type: 'confirm',
      title: 'Delete Stored Communication',
      message: 'Are you sure you want to delete this stored communication?',
      description: 'This action cannot be undone.',
      buttons: ['agree', 'disagree'],
      buttonLabels: {
        agree: 'Delete',
        disagree: 'Cancel',
      },
      onButtonClick: (button) => {
        if (button === 'agree') {
          void deleteStoredCommunication()
        }
      },
    })
  }

  if (status === 'processing' || isPolling) {
    return (
      <div className="communication-coach-display processing">
        <LoadingSpinner size="medium" />
        <p>Processing your request. This usually takes about 30-60 seconds.</p>
      </div>
    )
  }

  if (status === 'failed' || !result) {
    return (
      <div className="communication-coach-display error">
        <p>⚠️ {result?.message || 'Failed to generate results. Please try again.'}</p>
        <button onClick={onBack}>Back to Wizard</button>
      </div>
    )
  }

  const renderResult = () => {
    switch (action_type) {
      case 'email_response':
        return renderEmailLikeResult('Generated Email Response', result)
      case 'recruiter_outreach':
        return renderEmailLikeResult('Recruiter Outreach Draft', result)
      case 'cover_letter':
        return renderCoverLetterResult(result)
      case 'follow_up':
        return renderEmailLikeResult('Follow-Up Draft', result)
      case 'thank_you_note':
        return renderEmailLikeResult('Thank You Note', result)
      case 'interview_confirmation':
        return renderEmailLikeResult('Interview Confirmation', result)
      case 'offer_response':
        return renderEmailLikeResult('Offer Response', result)

      case 'message_review':
        return (
          <div className="result-message-review">
            <h3>Message Review</h3>
            {result.review_feedback?.tone_analysis && (
              <div className="result-section">
                <h4>Tone Analysis</h4>
                <p>{result.review_feedback.tone_analysis.assessment}</p>
                {result.review_feedback.tone_analysis.strengths && result.review_feedback.tone_analysis.strengths.length > 0 && (
                  <div>
                    <strong>Strengths:</strong>
                    <ul>
                      {result.review_feedback.tone_analysis.strengths.map((s, idx) => (
                        <li key={idx}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.review_feedback.tone_analysis.concerns && result.review_feedback.tone_analysis.concerns.length > 0 && (
                  <div>
                    <strong>Concerns:</strong>
                    <ul>
                      {result.review_feedback.tone_analysis.concerns.map((concern, idx) => (
                        <li key={idx}>{concern}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {renderListSection('Risk Flags', result.review_feedback?.risk_flags)}
            {result.review_feedback?.overall_feedback && (
              <div className="result-section">
                <h4>Overall Feedback</h4>
                <p>{result.review_feedback.overall_feedback}</p>
              </div>
            )}
            {result.review_feedback?.improved_version && (
              <div className="result-section">
                <h4>Improved Version</h4>
                <div className="result-body">{result.review_feedback.improved_version}</div>
              </div>
            )}
          </div>
        )

      case 'writing_assistance':
        return (
          <div className="result-writing-assistance">
            <h3>Writing Assistance</h3>
            {result.improved_text && (
              <div className="result-section">
                <h4>Improved Text</h4>
                <div className="result-body">{result.improved_text}</div>
              </div>
            )}
            {result.suggestions && result.suggestions.length > 0 && (
              <div className="result-section">
                <h4>Suggestions</h4>
                <ul>
                  {result.suggestions.map((s, idx) => (
                    <li key={idx}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.changes_explained && (
              <div className="result-section">
                <h4>Changes Explained</h4>
                <p>{result.changes_explained}</p>
              </div>
            )}
            {renderListSection('Writing Tips', result.writing_tips)}
            {renderListSection('Alternate Phrasings', result.alternate_phrasings)}
          </div>
        )

      case 'legal_review':
        return (
          <div className="result-legal-review">
            <h3>Legal Document Review</h3>
            {result.disclaimer && (
              <div className="result-section">
                <h4>Disclaimer</h4>
                <p>{result.disclaimer}</p>
              </div>
            )}
            {result.red_flags && result.red_flags.length > 0 && (
              <div className="result-section">
                <h4>Red Flags</h4>
                <ul>
                  {result.red_flags.map((flag, idx) => (
                    <li key={idx} className={`flag-${flag.severity || 'medium'}`}>
                      <strong>{flag.issue}</strong>
                      {flag.explanation && <p>{flag.explanation}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.missing_terms && result.missing_terms.length > 0 && (
              <div className="result-section">
                <h4>Missing Terms</h4>
                <ul>
                  {result.missing_terms.map((term, idx) => (
                    <li key={idx}>{term}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.negotiation_points && result.negotiation_points.length > 0 && (
              <div className="result-section">
                <h4>Negotiation Points</h4>
                <ul>
                  {result.negotiation_points.map((point, idx) => (
                    <li key={idx}>
                      <strong>{point.point}</strong>
                      {point.suggestion && <p>{point.suggestion}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.overall_assessment && (
              <div className="result-section">
                <h4>Overall Assessment</h4>
                <p>{result.overall_assessment}</p>
              </div>
            )}
            {renderListSection('Recommendations', result.recommendations)}
            {typeof result.should_consult_lawyer === 'boolean' && (
              <div className="result-section">
                <h4>Lawyer Review</h4>
                <p>{result.should_consult_lawyer ? 'A lawyer review is recommended before you sign.' : 'No immediate lawyer review was flagged, but confirm any high-risk terms.'}</p>
              </div>
            )}
          </div>
        )

      case 'salary_negotiation':
        return (
          <div className="result-salary-negotiation">
            <h3>Salary Negotiation Strategy</h3>
            {result.strategy && (
              <div className="result-section">
                <h4>Strategy</h4>
                <p>{result.strategy}</p>
              </div>
            )}
            {result.talking_points && result.talking_points.length > 0 && (
              <div className="result-section">
                <h4>Talking Points</h4>
                <ul>
                  {result.talking_points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.counter_offer_suggestions && (
              <div className="result-section">
                <h4>Counter-Offer Suggestions</h4>
                <p><strong>Salary Range:</strong> {result.counter_offer_suggestions.salary_range}</p>
                {result.counter_offer_suggestions.rationale && (
                  <p>{result.counter_offer_suggestions.rationale}</p>
                )}
              </div>
            )}
            {result.timing && (
              <div className="result-section">
                <h4>Timing</h4>
                <p>{result.timing}</p>
              </div>
            )}
            {renderListSection('Non-Salary Benefits', result.non_salary_benefits)}
            {renderListSection('Sample Scripts', result.sample_scripts)}
            {renderListSection('Risk Watchouts', result.risk_watchouts)}
          </div>
        )

      default:
        return (
          <div className="result-default">
            <p>Results are being processed...</p>
          </div>
        )
    }
  }

  return (
    <>
      <div className="communication-coach-display">
        <div className="display-header">
          <div className="display-header-actions">
            <button className="back-button" onClick={onBack}>
              ← {backLabel}
            </button>
            {canEditStoredCommunication && !isEditing && (
              <button
                className="secondary-button"
                onClick={() => {
                  resetEditableState()
                  setSaveError(null)
                  setIsEditing(true)
                }}
              >
                Edit
              </button>
            )}
            {canManageStoredCommunication && onDeleteStoredCommunication && (
              <button
                className="danger-button"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            {isEditing && (
              <>
                <button
                  className="secondary-button"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="primary-button"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
          <div className="display-meta">
            <span>{ACTION_TYPE_LABELS[action_type]}</span>
            {quality_tier && <span>{quality_tier === 'premium' ? 'Premium' : 'Standard'}</span>}
          </div>
        </div>
        {saveError && (
          <div className="display-inline-error">
            <p>⚠️ {saveError}</p>
          </div>
        )}
        <div className="display-content">
          {renderResult()}
        </div>
      </div>
      <MessageBox {...messageBox} />
    </>
  )
}
